import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl, apiUrl } from "../../../utils/apis";
import type {
  AdminStaff,
  AdminStaffPayload,
  UpdateAdminStaffPayload,
  AdminStaffApiResponse,
} from "./types";

export const adminStaffApi = createApi({
  reducerPath: "adminStaffApi",

  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem("token");
      headers.set("Content-Type", "application/json");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),

  tagTypes: ["AdminStaff"],

  endpoints: (builder) => ({
    // ✅ Get Admin Staff List
    getAdminStaffList: builder.query<
      AdminStaffApiResponse,
      Record<string, any> | void
    >({
      query: () => ({
        url: apiUrl.adminStaffApi,
        method: "GET",
      }),
      providesTags:[{ type: "AdminStaff", id: "LIST" }],
    }),

    // ✅ Get Admin Staff Details by ID
    getAdminStaffDetails: builder.query<AdminStaff, number | string>({
      query: (id) => ({
        url: `${apiUrl.adminStaffApi}${id}/`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [
        { type: "AdminStaff", id },
      ],
    }),

    // ✅ Create Admin Staff
    createAdminStaff: builder.mutation<AdminStaff, AdminStaffPayload>({
      query: (body) => ({
        url: apiUrl.adminStaffApi,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "AdminStaff", id: "LIST" }],
    }),

    // ✅ Update Admin Staff
    updateAdminStaff: builder.mutation<
      AdminStaff,
      { id: number | string; data: Partial<UpdateAdminStaffPayload> }
    >({
      query: ({ id, data }) => ({
        url: `${apiUrl.adminStaffApi}${id}/`,
        method: "PUT",
        body: data,
      }),
            invalidatesTags: [{ type: "AdminStaff", id: "LIST" }],

    }),

    // ✅ Delete Admin Staff
    deleteAdminStaff: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `${apiUrl.adminStaffApi}${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "AdminStaff", id: "LIST" }],
    }),

    updateAdminStaffStatus: builder.mutation<
      AdminStaff,
      { id: number | string; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({
        url: `manage/staff/${id}/`,
        method: "PUT",
        body: { is_active }
      }),
      invalidatesTags: ["AdminStaff"]
    }),
  

    // ✅ Search Admin Staff
    searchAdminStaff: builder.query<
      AdminStaffApiResponse,
      { search?: string; search_by?: string }
    >({
      query: ({ search = "", search_by = "" }) => ({
        url: apiUrl.adminStaffApi,
        params: { search, search_by },
      }),
      providesTags: [{ type: "AdminStaff", id: "LIST" }],
    }),
  }),
  
});

export const {
  useGetAdminStaffListQuery,
  useGetAdminStaffDetailsQuery,
  useCreateAdminStaffMutation,
  useUpdateAdminStaffMutation,
  useDeleteAdminStaffMutation,
  useLazySearchAdminStaffQuery,
    useUpdateAdminStaffStatusMutation

} = adminStaffApi;
